import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ControlOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Popover, Space, Table } from 'antd';

import { SubscriptionAccessWithChildren } from '@app/domain-subscription-interfaces';
import { Tables } from '@app/web-components';

import { useFindSubscriptionAccess } from '../data/subscription-accesses';

import { EligibilityRenderer } from './eligibility-renderer';
import { SUBSCRIPTION_ACCESS_TAB, TAB_SEARCH_PARAM } from './manage-page';

export type SubscriptionAccessPopoverProps = {
  bundleId: number;
  bundleAccessId: number;
  disabled?: boolean;
  children: ReactNode;
};

export const SubscriptionAccessPopover = ({
  bundleId,
  bundleAccessId,
  disabled = false,
  children,
}: SubscriptionAccessPopoverProps) => {
  const navigate = useNavigate();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { getQuery, getTableProps } = Tables.useTableState<SubscriptionAccessWithChildren>(
    'Subscription Access Popover',
    [
      {
        title: 'User',
        dataIndex: ['subscription', 'user', 'name'],
        ellipsis: true,
        sorter: true,
        defaultSortOrder: 'ascend',
      },
      {
        title: 'Is Eligible',
        width: 120,
        dataIndex: 'isEligible',
        sorter: true,
        render: (isEligible: boolean, record) => (
          <EligibilityRenderer isEligible={isEligible} eligibility={record.eligibility} />
        ),
        filters: [
          { text: 'True', value: true },
          { text: 'False', value: false },
        ],
        filterMultiple: false,
      },
    ],
    { disableSession: true },
  );

  const queryResult = useFindSubscriptionAccess(getQuery({ where: { bundleAccessId } }), !disabled);

  if (disabled) return children;

  return (
    <Popover
      title={
        <>
          <span>Subscribers</span>
          <Space direction="horizontal" size="small" style={{ float: 'right' }}>
            <Button
              icon={<ControlOutlined />}
              size="small"
              type="link"
              title="Manage this bundle's access subscriptions"
              onClick={() => navigate(`./${bundleId}/manage?${TAB_SEARCH_PARAM}=${SUBSCRIPTION_ACCESS_TAB}`)}
            >
              Manage Access Subscriptions
            </Button>
            <Button
              icon={<ReloadOutlined />}
              size="small"
              type="link"
              title="Refresh data."
              onClick={() => {
                queryResult.refetch();
              }}
            >
              Refresh
            </Button>
          </Space>
        </>
      }
      content={
        <div style={{ width: 450, paddingTop: 10 }}>
          <Table
            {...getTableProps(queryResult)}
            scroll={{
              y: 250,
            }}
          />
        </div>
      }
      trigger="hover"
      onOpenChange={(open) => setIsPopoverOpen(open)}
      open={isPopoverOpen}
    >
      {children}
    </Popover>
  );
};
